import React from "react";
import { Alert, Button, Result, Tag } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { dashboardV2Url, PAGES_MOVED_TO_V2 } from "configs/DashboardV2Config";

/**
 * Os avisos de "isto foi para a dashboard v2" — a página inteira, um painel ou uma aba, e a marca
 * que vai no cabeçalho de painel. O que migrou está em `configs/DashboardV2Config`.
 *
 * O link abre em outra aba: quem está aqui ainda usa a antiga para o que não migrou.
 */

const OpenInV2Button = ({ href, label }: { href: string; label: string }) => (
  <Button type="primary" icon={<ExportOutlined />} href={href} target="_blank" rel="noopener noreferrer">
    {label}
  </Button>
);

/** A página migrou inteira: o aviso ocupa o lugar dela. */
export const MovedToV2Page = ({ pageKey }: { pageKey: string }) => {
  const page = PAGES_MOVED_TO_V2[pageKey];

  return (
    <Result
      status="info"
      title={`${page.title} está na dashboard v2`}
      subTitle="Esta página não funciona mais aqui. Use a mesma tela na dashboard v2."
      extra={<OpenInV2Button href={dashboardV2Url(page.v2Path)} label="Abrir na dashboard v2" />}
    />
  );
};

/** Uma parte da página migrou: o aviso fica no lugar dela, e o resto da página continua. */
export const MovedToV2Notice = ({
  title,
  description,
  href,
  linkLabel = "Abrir na dashboard v2",
}: {
  title: string;
  description?: React.ReactNode;
  href: string;
  linkLabel?: string;
}) => (
  <Alert
    type="info"
    showIcon
    message={title}
    description={
      <>
        {description}
        <div style={{ marginTop: description ? 12 : 0 }}>
          <OpenInV2Button href={href} label={linkLabel} />
        </div>
      </>
    }
  />
);

/** No cabeçalho de um painel que migrou, para o operador saber antes de abrir. */
export const MovedToV2Tag = () => (
  <Tag color="blue" style={{ marginLeft: 8 }}>
    na dashboard v2
  </Tag>
);

// Um componente por página, criado uma vez: a rota recebe sempre o mesmo, e não remonta a cada render.
const movedPageComponents: Record<string, React.ComponentType> = {};

/**
 * Para a rota: a página, ou o aviso no lugar dela se ela migrou. A página continua no menu e na rota
 * — só o conteúdo muda.
 */
export function pageOrMovedToV2<T>(pageKey: string, page: T): T | React.ComponentType {
  if (!PAGES_MOVED_TO_V2[pageKey]) return page;

  if (!movedPageComponents[pageKey]) {
    const Moved = () => <MovedToV2Page pageKey={pageKey} />;
    Moved.displayName = `MovedToV2(${pageKey})`;
    movedPageComponents[pageKey] = Moved;
  }

  return movedPageComponents[pageKey];
}
